import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ActivityPage } from '../claudeagents/claudeAgentApi';

export interface PagedActivity<T> {
  items: T[];
  /** Every record in the range, when the API reports it; otherwise what's loaded. */
  total: number;
  loading: boolean;
  /** The first page failed to load. */
  failed: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  moreFailed: boolean;
  loadMore: () => void;
}

/**
 * An activity list loaded a page at a time. Reloads from the first page whenever
 * `resetKey` changes; pass a null `fetchPage` to clear the list without loading.
 * Responses for an older `resetKey` (agent or date range changed mid-load) are dropped.
 */
export function usePagedActivity<T extends { id: string | number }>(
  fetchPage: ((offset: number) => Promise<ActivityPage<T>>) | null,
  resetKey: string,
): PagedActivity<T> {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const generationRef = useRef(0);
  const loadingRef = useRef(false);
  // The resetKey the effect below last started loading; until it catches up
  // with a new key, the list is already loading (no empty-list flash).
  const startedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    startedKeyRef.current = resetKey;
    const generation = ++generationRef.current;
    loadingRef.current = false;
    setItems([]);
    setTotal(undefined);
    setHasMore(false);
    setFailed(false);
    setMoreFailed(false);
    setLoadingMore(false);
    const fetch = fetchRef.current;
    if (!fetch) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(0)
      .then((page) => {
        if (generation !== generationRef.current) return;
        setItems(page.items);
        setTotal(page.total);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        if (generation !== generationRef.current) return;
        setItems([]);
        setFailed(true);
      })
      .finally(() => {
        if (generation === generationRef.current) setLoading(false);
      });
  }, [resetKey]);

  const loadMore = useCallback(() => {
    const fetch = fetchRef.current;
    if (!fetch || loadingRef.current || !hasMore || loading) return;
    const generation = generationRef.current;
    loadingRef.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    fetch(items.length)
      .then((page) => {
        if (generation !== generationRef.current) return;
        // Rows added since the first page shift offsets, so the next page can repeat one.
        setItems((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...page.items.filter((r) => !seen.has(r.id))];
        });
        if (page.total !== undefined) setTotal(page.total);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        if (generation === generationRef.current) setMoreFailed(true);
      })
      .finally(() => {
        if (generation !== generationRef.current) return;
        loadingRef.current = false;
        setLoadingMore(false);
      });
  }, [items.length, hasMore, loading]);

  const pending = fetchPage !== null && startedKeyRef.current !== resetKey;
  return { items, total: total ?? items.length, loading: loading || pending, failed, loadingMore, hasMore, moreFailed, loadMore };
}

/**
 * Sits at the end of a scrollable list (its parent) and asks for the next page
 * when it scrolls into view. Shows a loading row, or a retry after a failed page.
 */
export const LoadMoreSentinel: React.FC<{ list: PagedActivity<unknown> }> = ({ list }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { hasMore, moreFailed, loadingMore, loadMore, items } = list;

  useEffect(() => {
    const sentinel = ref.current;
    if (!sentinel || !hasMore || moreFailed) return;
    // Re-created after each page so a list that still doesn't fill the panel keeps loading.
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root: sentinel.parentElement, rootMargin: '0px 0px 120px 0px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, moreFailed, loadMore, items.length]);

  if (!hasMore && !moreFailed) return null;
  return (
    <div ref={ref} className="dw-ov-task-list__more" aria-live="polite">
      {moreFailed ? (
        <>
          Couldn't load more.
          <button type="button" className="dw-ov-task-list__retry" onClick={loadMore}>Retry</button>
        </>
      ) : loadingMore ? 'Loading more…' : null}
    </div>
  );
};
